import { Component, inject, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth.service';
import { ReportLabelsService } from './report-labels.service';
import { ReportLabel } from './report-labels.model';
import { OrganizationDetailModel } from '../../organizations/organization.interface';
import { NzTableModule } from 'ng-zorro-antd/table';
import { SvgIconComponent } from 'angular-svg-icon';
import { CommonModule } from '@angular/common';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { FormsModule } from '@angular/forms';
import { CommonService } from '../../../shared/services/common.service';
import { ErrorResponse } from '../../../core/models/error.models';
import { ActivatedRoute } from '@angular/router';
import { OrganizationSelectionService } from '../../../core/services/organization-selection.service';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { ManageOrganizationDrawerParams } from '../../manage-organization/manage-organization.interface';
import { DrawerService } from '../../../core/services/drawer.service';
import { OrgManagementMode } from '../../manage-organization/manage-organization.enums';

@Component({
  selector: 'app-report-labels',
  standalone: true,
  imports: [
    NzTableModule,
    SvgIconComponent,
    CommonModule,
    NzInputModule,
    NzButtonModule,
    FormsModule,
  ],
  templateUrl: './report-labels.component.html',
  styleUrls: ['./report-labels.component.scss'],
})
export class ReportLabelsComponent implements OnInit {
  private readonly commonService = inject(CommonService);

  loading$: Observable<boolean>;
  reportLabels$: Observable<ReportLabel[]>;

  private readonly destroy$ = new Subject<void>();

  currentEditingItem: ReportLabel | null = null;

  orgData!: OrganizationDetailModel;
  organizationId!: number;

  TABLE_COLUMNS = [
    {
      title: 'Token',
      key: 'labelKey',
    },
    {
      title: 'Token Value',
      key: 'overrideValue',
    },
    {
      title: 'Action',
      key: 'action',
    },
  ];
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
  };

  constructor(
    private authService: AuthService,
    private reportLabelsService: ReportLabelsService,
    private route: ActivatedRoute,
    private orgSelectionService: OrganizationSelectionService,
    private readonly drawerService: DrawerService
  ) {
    this.loading$ = this.reportLabelsService.loading$;
    this.reportLabels$ = this.reportLabelsService.reportLabels$;
  }

  ngOnInit(): void {
    this.setupDrawerParams();
    this.subscribeToOrgData();
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  private subscribeToOrgData(): void {
    this.orgSelectionService.orgId$
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(res => {
        if (res) {
          this.organizationId = res;
          this.reportLabelsService.loadReportLabels(this.organizationId);
        } else {
          if (this.orgParams.organizationId) {
            const orgId = Number(this.orgParams.organizationId);
            this.organizationId = orgId;
            this.orgData = {
              organizationId: orgId,
            } as OrganizationDetailModel;
            this.reportLabelsService.loadReportLabels(this.organizationId);
          }
        }
      });
  }

  toggleEdit(item: ReportLabel): void {
    if (this.currentEditingItem && this.currentEditingItem !== item) {
      this.currentEditingItem.isEditing = false;
      this.currentEditingItem.overrideValue =
        this.currentEditingItem.originalValue ?? '';
    }

    this.currentEditingItem = item;
    item.isEditing = true;
    item.originalValue = item.overrideValue;
  }

  cancelEdit(item: ReportLabel): void {
    item.isEditing = false;
    item.overrideValue = item.originalValue ?? '';
    this.currentEditingItem = null;
  }

  saveChanges(item: ReportLabel): void {
    if (item.overrideValue !== item.originalValue) {
      item.isEditing = false;
      const reportLabel = {
        id: item.id,
        organizationId: this.organizationId,
        labelKeyId: item.labelKeyId,
        overrideValue: item.overrideValue,
      };
      this.reportLabelsService.createReportLabel(reportLabel).subscribe({
        next: () => {
          this.commonService.notify('success', 'Data saved successfully');
          this.reportLabelsService.loadReportLabels(this.organizationId);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
    } else {
      item.isEditing = false;
    }

    this.currentEditingItem = null;
  }

  // isLabelDisabled(data: ReportLabel): boolean {
  //   return [99, 101].includes(data.labelKeyId);
  // }

  getCurrentLength(data: ReportLabel): number {
    return data.overrideValue?.length || 0;
  }

  checkCharacterCount(data: ReportLabel): void {
    const currentLength = this.getCurrentLength(data);
    if (data.length && currentLength > data.length) {
      data.overrideValue = data.overrideValue?.substring(0, data.length);
    }
  }
}
